import React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, 
  Button, Box, TextField, Typography, Snackbar, Alert} from "@mui/material";
import { Stack } from "@mui/system";
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { get } from 'aws-amplify/api';
import * as Utils from '../util/Utils'
import {ProjectContext} from "../schedule/ProjectContext";

export default function DeployScheduleDialog(
  props: {
    open: boolean,
    scheduleFilename: string,
    project: string,
    handleDeploy: () => void,
    handleCancel: () => void,
  }
) {

  const projectContext = React.useContext(ProjectContext);

  const [fullFilename, setFullFilename] = React.useState('');
  const [scheduleContent, setScheduleContent] = React.useState('');
  const [validation, setValidation] = React.useState<string[]>([]);
  const [snackInfo, setSnackInfo] = React.useState<any>({
    open: false, message: ''
  });

  React.useEffect(() => {
    setScheduleContent('');
    if (props.open) {
      const getOperation = get(
        {
          apiName: 'cabb',
          path: '/get_deploy_file',
          options: {
            queryParams: {
              project: props.project,
              schedule_file: props.scheduleFilename
            }
          }
        }
      );

      getOperation.response
        .then(response => response.body.json())
        .then((response: any) => {
          if (response['status'] === 'success') {
            setFullFilename(response['filename']);
            setScheduleContent(JSON.stringify(response['content'], null, 4));
          } else {
            setSnackInfo({open: true, message: "Error: " + response['message']});
          }
        })
        .catch((err: any) => {
          setSnackInfo({open: true, message: "Error: " + err.message});
        });
    }
  }, [props.open]);
    
  React.useEffect(() => {
    if (scheduleContent) {
      handleValidate();
    }
  }, [scheduleContent]);

  function handleValidate() {
    const schedule = JSON.parse(scheduleContent);
    const errors = Utils.validateScheduleFile(schedule, projectContext.correlatorSettings);
    setValidation(errors);
  }

  const handleCancel = () => {
    props.handleCancel();
  }

  const handleDeploy = () => {
    props.handleDeploy();
  }

  const handleDownload = () => {
    let filename = fullFilename
    var element = document.createElement('a');
    element.setAttribute('href', 'data:text/plain;charset=utf-8,' + scheduleContent);
    element.setAttribute('download', filename);

    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  return (
    <Dialog open={props.open} PaperProps={{ sx: {height: '80%'} }}
      aria-labelledby="deploy-schedule-dialog-title"
      onClose={handleCancel}
      maxWidth="md" fullWidth>
      <DialogTitle id="deploy-schedule-dialog-title">
        {`Deploy ${fullFilename} to ATCA`}
      </DialogTitle>
      <DialogContent sx={{height: '100%'}}>
        <Stack spacing={3} sx={{ height: '100%' }}>
          <Stack direction={'row'} alignItems={'flex-end'}
          justifyContent={'space-between'}>
            <TextField id="atca_filename" 
              label="File name deployed to ATCA" 
              variant="standard" 
              value={fullFilename}
              sx={{width: '80%'}}
            />
            <Button variant="text"
              onClick={handleDownload}
              startIcon={<DownloadOutlinedIcon />}>
              Download
            </Button>            
          </Stack>
          <Box
            sx={{ 
              border: '1px solid #bdbdbd',
              borderRadius: '10px',
              padding: '10px',
              height: '100%',
              overflow: 'scroll'
            }}
          >
            <pre style={{width:'100%'}}>
              {scheduleContent}
            </pre>
          </Box>

          { validation.length>0 &&
          <Box
            sx={{ 
              border: '1px solid #bdbdbd',
              borderRadius: '10px',
              padding: '10px',
              height: '100%',
              overflow: 'scroll'
            }}
          >
            <pre style={{width:'100%'}}>
              { JSON.stringify(validation, null, 4) }
            </pre>
          </Box>
          }
        </Stack>
      </DialogContent>

      <DialogActions>
        <Typography color={'#d32f2f'} sx={{flexGrow: 4}}>
          {validation.length>0 ? "There are errors with the schedule file." : ""}
        </Typography>
        <Button autoFocus color={validation.length>0 ? 'error' : 'success'}
          onClick={handleDeploy}>
          {validation.length>0 ? "Deploy with Errors" : "Deploy"}
        </Button>
        <Button onClick={handleCancel}>
          Cancel
        </Button>
      </DialogActions>

      <Snackbar
        role = "alert"
        aria-label = {"Alert message"}
        open={snackInfo['open']}
        autoHideDuration={6000}
        onClose={e => setSnackInfo({open: false, message: ''})}
      >
        <Alert severity="info">
          {snackInfo['message']}
        </Alert>
      </Snackbar>
            
    </Dialog>
  );
};
